import { Template } from 'src/api/template';
import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import { listSubject } from 'src/api/subject';
import { normalizeLabels } from 'src/page/Subjects/detail/hierarchyUtils';
import {
  HierarchyTopicGroup,
  HierarchyTopicSelection,
} from 'src/types/hierarchyTopic';

export type ISubjects = {
  id: string;
  name: string;
  level_labels: string[];
};

type PageType = 'edit' | 'create';

type TemplateContextType = {
  selectedSubject: string;
  setSelectedSubject: React.Dispatch<React.SetStateAction<string>>;
  topicGroup: HierarchyTopicGroup[];
  setTopicGroup: React.Dispatch<React.SetStateAction<HierarchyTopicGroup[]>>;
  steps: number;
  setSteps: React.Dispatch<React.SetStateAction<number>>;
  subjects: ISubjects[];
  selectedSubjectMeta: ISubjects | null;
  isSaveModalOpen: boolean;
  setIsSaveModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  name: string;
  setName: React.Dispatch<React.SetStateAction<string>>;
  thumbnail: File | null;
  setThumbnail: React.Dispatch<React.SetStateAction<File | null>>;
  thumbnailPreviewUrl: string | null;
  setThumbnailPreviewUrl: React.Dispatch<React.SetStateAction<string | null>>;
  clearFormValues: () => void;
  populateFormValue: (data: Template) => void;
  pageType: PageType;
  setPageType: (type: PageType) => void;
  isGettingExtraInfo: boolean;
  setValidationError: (value: string | null) => void;
  validationError: string | null;
};

const TemplateContext = createContext<TemplateContextType | undefined>(
  undefined
);

export const TemplateDataProvider = ({ children }: { children: ReactNode }) => {
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [topicGroup, setTopicGroup] = useState<HierarchyTopicGroup[]>([]);
  const [steps, setSteps] = useState(1);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [pageType, setPageType] = useState<PageType>('create');
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string | null>(
    null
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const [subjects, setSubjects] = useState<ISubjects[]>([]);
  const [isGettingExtraInfo, setIsGettingExtraInfo] = useState(false);

  const selectedSubjectMeta =
    subjects.find((subject) => subject.id === selectedSubject) ?? null;

  const clearFormValues = () => {
    setSelectedSubject('');
    setTopicGroup([]);
    setSteps(1);
    setIsSaveModalOpen(false);
    setName('');
    setThumbnail(null);
    setThumbnailPreviewUrl(null);
  };

  const populateFormValue = (data: Template) => {
    setSteps(3);
    setSelectedSubject(data.subject);
    setTopicGroup(data.topic_groups as HierarchyTopicGroup[]);
    setName(data.name);
    setThumbnail(null);
    setThumbnailPreviewUrl(data.thumbnail ?? null);
  };

  const fetchSubjects = async () => {
    try {
      setIsGettingExtraInfo(true);
      const res = await listSubject(1, '', 100000);
      const apiSubjects = res.data?.data?.subjects ?? [];
      setSubjects(
        apiSubjects.map((s: any) => ({
          id: s.id,
          name: s.title || '',
          level_labels: normalizeLabels(s.level_labels),
        }))
      );
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
      setSubjects([]);
    } finally {
      setIsGettingExtraInfo(false);
    }
  };

  useEffect(() => {
    if (subjects.length > 0) return;
    fetchSubjects();
  }, []);

  return (
    <TemplateContext.Provider
      value={{
        selectedSubject,
        setSelectedSubject,
        topicGroup,
        setTopicGroup,
        steps,
        setSteps,
        subjects,
        selectedSubjectMeta,
        isSaveModalOpen,
        setIsSaveModalOpen,
        name,
        setName,
        thumbnail,
        setThumbnail,
        thumbnailPreviewUrl,
        setThumbnailPreviewUrl,
        clearFormValues,
        populateFormValue,
        pageType,
        setPageType,
        isGettingExtraInfo,
        validationError,
        setValidationError,
      }}
    >
      {children}
    </TemplateContext.Provider>
  );
};

export const useTemplateData = () => {
  const context = useContext(TemplateContext);
  if (!context)
    throw new Error('useTemplate must be used within a TemplateProvider');
  return context;
};

export type { HierarchyTopicSelection };
